// ParameterFormBar — yeni-design styled, collapsible parameter inputs.
//
// Layout:
//  - Always-visible header bar: filter icon + "Filtreler" title + active-filter
//    count badge + (when collapsed) up-to-3 active-value chips + Sıfırla +
//    Sorgula + chevron toggle. Header is always interactive, even when body
//    is collapsed — so a quick re-run never requires expanding.
//  - Collapsible body: responsive grid of inputs based on each parameter's
//    type (date / number / bool / string).
//
// "Active filter" = a value that differs from the parameter's computed default.
// Empty strings are NOT counted as active (they map to "all rows" semantics).
import {
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
} from 'react';
import dayjs from 'dayjs';
import { Button, Input } from './yeni-design/primitives';
import { I } from './yeni-design/icons';
import type { ParameterConfig, ParameterUiType } from '../types/panelConfig';

export type ParamValues = Record<string, string>;

/**
 * Computes opinionated defaults for each parameter:
 *  - explicit `default` override wins (sentinel "today" → today's date for date params)
 *  - date: today (YYYY-MM-DD)
 *  - number-like: "0"
 *  - bool: "false"
 *  - string: "" (empty — sproc auto-default behavior preserved; user can override)
 */
export function computeDefaults(parameters: ParameterConfig[]): ParamValues {
  const today = dayjs().format('YYYY-MM-DD');
  const out: ParamValues = {};
  for (const p of parameters) {
    out[p.key] = computeOneDefault(p, today);
  }
  return out;
}

function computeOneDefault(p: ParameterConfig, today: string): string {
  if (p.default != null && p.default !== '') {
    if (isDateType(p.type) && p.default.toLowerCase() === 'today') return today;
    return p.default;
  }
  if (isDateType(p.type)) return today;
  if (isNumericType(p.type)) return '0';
  if (isBoolType(p.type)) return 'false';
  return '';
}

export function isDateType(t: ParameterUiType): boolean {
  return t === 'date';
}
export function isNumericType(t: ParameterUiType): boolean {
  return t === 'int' || t === 'decimal' || t === 'float' || t === 'double' || t === 'number';
}
export function isBoolType(t: ParameterUiType): boolean {
  return t === 'bool' || t === 'boolean';
}

function formatChipValue(p: ParameterConfig, value: string): string {
  if (isBoolType(p.type)) return value === 'true' || value === '1' ? 'Evet' : 'Hayır';
  if (isDateType(p.type) && value) {
    const d = dayjs(value);
    if (d.isValid()) return d.format('DD.MM.YYYY');
  }
  return value;
}

interface ParameterFormBarProps {
  parameters: ParameterConfig[];
  values: ParamValues;
  onChange: (values: ParamValues) => void;
  onSubmit: () => void;
  onReset?: () => void;
  isLoading?: boolean;
  rowCount?: number;
  /** Initial collapsed state — defaults to false (open). */
  defaultCollapsed?: boolean;
}

export function ParameterFormBar({
  parameters,
  values,
  onChange,
  onSubmit,
  onReset,
  isLoading,
  rowCount,
  defaultCollapsed = false,
}: ParameterFormBarProps) {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [open, setOpen] = useState(!defaultCollapsed);

  const today = useMemo(() => dayjs().format('YYYY-MM-DD'), []);

  // "Active" = value differs from computed default and is non-empty.
  const activeFilters = useMemo(() => {
    return parameters.filter((p) => {
      const v = values[p.key] ?? '';
      if (v === '') return false;
      const def = computeOneDefault(p, today);
      return v !== def;
    });
  }, [parameters, values, today]);

  if (parameters.length === 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 bg-surface border border-border rounded-md text-[12px] text-ink-500 mb-3">
        <I.Filter size={12} />
        <span>Bu rapor için filtre yok.</span>
        {typeof rowCount === 'number' && (
          <span className="ml-auto">
            <strong className="tnum text-ink-900">{rowCount}</strong> kayıt
          </span>
        )}
      </div>
    );
  }

  const update = (key: string, value: string) => {
    onChange({ ...values, [key]: value });
    setTouched((t) => ({ ...t, [key]: true }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  const handleQuickSubmit = () => onSubmit();

  // Up to 3 active-value chips when collapsed (avoid overflow).
  const visibleChips = activeFilters.slice(0, 3);
  const extraChipCount = activeFilters.length - visibleChips.length;

  return (
    <div className="bg-surface border border-border rounded-md mb-3 overflow-hidden">
      {/* Header bar — always visible */}
      <div className="flex items-center gap-3 px-3 py-2 bg-ink-50/50">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-2 bg-transparent border-0 p-0 m-0 font-inherit text-inherit cursor-pointer hover:text-ink-900"
          aria-label={open ? 'Filtre panelini kapat' : 'Filtre panelini aç'}
        >
          <I.Filter size={13} className="text-ink-500" />
          <span className="text-[12px] font-semibold text-ink-700">Filtreler</span>
          {activeFilters.length > 0 && (
            <span className="inline-flex items-center bg-brand-50 border border-brand-200 text-brand-700 rounded text-[10.5px] px-1.5 py-0.5 font-medium mono">
              {activeFilters.length} aktif
            </span>
          )}
          <span className="text-ink-400 ml-1">
            {open ? <I.ChevronUp size={12} /> : <I.ChevronDown size={12} />}
          </span>
        </button>

        {/* Active-filter chips — only when collapsed and there are active filters */}
        {!open && visibleChips.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap min-w-0">
            {visibleChips.map((p) => (
              <ActiveChip
                key={p.key}
                param={p}
                value={values[p.key] ?? ''}
                onClear={onReset ? () => update(p.key, computeOneDefault(p, today)) : undefined}
              />
            ))}
            {extraChipCount > 0 && (
              <span className="text-[11px] text-ink-500 mono">+{extraChipCount}</span>
            )}
          </div>
        )}

        {/* Spacer + status + actions */}
        <div className="ml-auto flex items-center gap-2">
          {typeof rowCount === 'number' && !isLoading && (
            <span className="text-[12px] text-ink-500 hidden sm:inline">
              <strong className="tnum text-ink-900">{rowCount}</strong> kayıt
            </span>
          )}
          {isLoading && (
            <span className="text-[12px] text-ink-500 inline-flex items-center gap-1.5">
              <I.Refresh size={12} className="animate-spin" />
              Yükleniyor…
            </span>
          )}
          {onReset && activeFilters.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onReset}
              disabled={isLoading}
            >
              Sıfırla
            </Button>
          )}
          <Button
            type="button"
            variant="primary"
            size="sm"
            icon={I.Search}
            onClick={handleQuickSubmit}
            disabled={isLoading}
          >
            Sorgula
          </Button>
        </div>
      </div>

      {/* Collapsible body */}
      {open && (
        <form onSubmit={handleSubmit} className="border-t border-border px-5 py-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-4">
            {parameters.map((p) => (
              <ParameterField
                key={p.key}
                param={p}
                value={values[p.key] ?? ''}
                onChange={(v) => update(p.key, v)}
                invalid={p.required && touched[p.key] && (values[p.key] ?? '').trim() === ''}
              />
            ))}
          </div>
          {/* Hidden submit so Enter on inputs still triggers query */}
          <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
        </form>
      )}
    </div>
  );
}

interface ActiveChipProps {
  param: ParameterConfig;
  value: string;
  onClear?: () => void;
}

function ActiveChip({ param, value, onClear }: ActiveChipProps): ReactNode {
  const label = param.label && param.label.trim().length > 0 ? param.label : param.key;
  return (
    <span className="inline-flex items-center gap-1 bg-white border border-border rounded text-[11px] px-1.5 py-0.5 max-w-[200px]">
      <span className="text-ink-500 font-medium truncate">{label}:</span>
      <span className="text-ink-900 mono truncate">{formatChipValue(param, value)}</span>
      {onClear && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          className="text-ink-400 hover:text-ink-700 bg-transparent border-0 p-0 m-0 cursor-pointer"
          aria-label="Filtreyi temizle"
        >
          <I.X size={9} strokeWidth={2.5} />
        </button>
      )}
    </span>
  );
}

interface ParameterFieldProps {
  param: ParameterConfig;
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
}

function ParameterField({ param, value, onChange, invalid }: ParameterFieldProps) {
  const labelText = param.label && param.label.trim().length > 0 ? param.label : param.key;
  const labelClass = `block text-[12px] font-medium text-ink-700 mb-1.5 truncate ${
    invalid ? 'text-neg-700' : ''
  }`;

  return (
    <label className="block min-w-0">
      <span className={labelClass}>
        {labelText}
        {param.required && <span className="text-neg-600 ml-0.5">*</span>}
      </span>
      {renderInput(param, value, onChange)}
    </label>
  );
}

const inputBaseCls =
  'w-full bg-white border border-border rounded-md px-3 h-9 text-[13px] text-ink-900 placeholder:text-ink-400 outline-none transition-shadow focus:border-brand-600 focus:shadow-[0_0_0_3px_rgba(29,78,216,0.12)]';

function renderInput(
  param: ParameterConfig,
  value: string,
  onChange: (v: string) => void,
) {
  const handle = (e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value);

  if (isDateType(param.type)) {
    return (
      <input
        type="date"
        value={value}
        onChange={handle}
        className={inputBaseCls}
      />
    );
  }
  if (isNumericType(param.type)) {
    return (
      <input
        type="number"
        value={value}
        onChange={handle}
        placeholder="0"
        className={`${inputBaseCls} mono`}
      />
    );
  }
  if (isBoolType(param.type)) {
    const checked = value === 'true' || value === '1';
    return (
      <button
        type="button"
        onClick={() => onChange(checked ? 'false' : 'true')}
        className={`focus-ring w-full inline-flex items-center justify-between bg-white border rounded-md px-3 h-9 text-[13px] transition-shadow outline-none focus:shadow-[0_0_0_3px_rgba(29,78,216,0.12)] ${
          checked
            ? 'border-brand-600 text-brand-700'
            : 'border-border text-ink-700 hover:border-border-strong'
        }`}
      >
        <span>{checked ? 'Evet' : 'Hayır'}</span>
        <span
          className={`inline-flex w-9 h-5 rounded-full p-0.5 transition-colors ${
            checked ? 'bg-brand-600' : 'bg-ink-200'
          }`}
        >
          <span
            className={`inline-block w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
              checked ? 'translate-x-4' : ''
            }`}
          />
        </span>
      </button>
    );
  }
  return (
    <Input
      value={value}
      onChange={handle}
      placeholder="(boş — tüm kayıtlar)"
      size="md"
    />
  );
}
